<template>
  <div v-if="open" class="ncm-overlay" @click.self="emitClose" role="dialog" aria-modal="true">
    <div class="ncm-container">
      <header class="ncm-header">
        <div class="ncm-title-wrap">
          <span class="ncm-arrow" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 17 L17 7" />
              <path d="M9 7 L17 7 L17 15" />
            </svg>
          </span>
          <div>
            <h2 class="ncm-title">Notification settings</h2>
            <p class="ncm-subtitle">Pick which campaign events alert you, and how. Email is the escalation channel for things that need your eyes on them.</p>
          </div>
        </div>
        <button class="ncm-close" @click="emitClose" aria-label="Close">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
            <path d="M6 6 L18 18" />
            <path d="M18 6 L6 18" />
          </svg>
        </button>
      </header>

      <div class="ncm-scope-row">
        <label class="ncm-scope-label" for="ncm-scope-select">Apply to</label>
        <div class="ncm-select-wrap">
          <select
            id="ncm-scope-select"
            class="ncm-scope-select"
            :value="activeScope"
            @change="onScopeChange(($event.target as HTMLSelectElement).value)"
          >
            <option value="default">Default — applies to all campaigns</option>
            <optgroup v-if="campaigns.length" label="Override for a specific campaign">
              <option
                v-for="c in campaigns"
                :key="c.id"
                :value="c.id"
              >{{ c.name || 'Untitled' }}{{ c.paused ? ' (paused)' : '' }}</option>
            </optgroup>
          </select>
          <svg class="ncm-select-caret" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
            <path d="M6 9 L12 15 L18 9" />
          </svg>
        </div>
        <button
          class="ncm-revert-all"
          @click="onResetScope"
          :title="activeScope === 'default' ? 'Reset default policy to catalogue defaults' : `Clear all overrides for ${currentScopeLabel}`"
        >{{ activeScope === 'default' ? 'Reset to defaults' : 'Clear overrides' }}</button>
      </div>

      <div v-if="campaignsLoading" class="ncm-empty">Loading campaigns…</div>
      <div v-else-if="campaignsError" class="ncm-empty ncm-empty--error">
        Couldn't load campaigns: {{ campaignsError }}
      </div>

      <div class="ncm-scroll">
        <div
          v-for="group in CREATOR_NOTIFICATION_GROUPS"
          :key="group.id"
          class="ncm-group"
        >
          <header class="ncm-group__header">
            <span class="ncm-arrow ncm-arrow--mini" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7 17 L17 7" />
                <path d="M9 7 L17 7 L17 15" />
              </svg>
            </span>
            <div>
              <h3>{{ group.label }}</h3>
              <p>{{ group.description }}</p>
            </div>
          </header>
          <ul class="ncm-event-list">
            <li
              v-for="entry in listNotificationsByGroup(group.id)"
              :key="entry.id"
              class="ncm-event"
            >
              <div class="ncm-event__meta">
                <div class="ncm-event__title-row">
                  <span class="ncm-event__title">{{ entry.label }}</span>
                  <button
                    v-if="activeScope !== 'default' && store.hasOverride(activeScope, entry.id)"
                    class="ncm-event__override"
                    @click="store.clearOverride(activeScope, entry.id)"
                    title="Click to revert this event to the default policy"
                  >Override · revert</button>
                </div>
                <div class="ncm-event__desc">{{ entry.description }}</div>
              </div>
              <div class="ncm-event__toggles">
                <button
                  type="button"
                  class="ncm-toggle ncm-toggle--in-app"
                  :class="{ 'ncm-toggle--on': policyOf(entry.id).inApp }"
                  :title="`In-app: ${policyOf(entry.id).inApp ? 'on' : 'off'}`"
                  @click="setChannel(entry.id, 'inApp', !policyOf(entry.id).inApp)"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M6.5 17 L17.5 17" />
                    <path d="M7.5 17 L7.5 11 C7.5 8.2 9.5 5.5 12 5.5 C14.5 5.5 16.5 8.2 16.5 11 L16.5 17" />
                    <path d="M10.2 19 C10.2 20.1 11 21 12 21 C13 21 13.8 20.1 13.8 19" />
                  </svg>
                  <span class="ncm-toggle__label">Bell</span>
                </button>
                <button
                  type="button"
                  class="ncm-toggle ncm-toggle--email"
                  :class="{ 'ncm-toggle--on': policyOf(entry.id).email }"
                  :title="`Email: ${policyOf(entry.id).email ? 'on' : 'off'}`"
                  @click="setChannel(entry.id, 'email', !policyOf(entry.id).email)"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="3" y="5.5" width="18" height="13" rx="2" />
                    <path d="M3.5 6.5 L12 13 L20.5 6.5" />
                  </svg>
                  <span class="ncm-toggle__label">Email</span>
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <footer class="ncm-footer">
        <span class="ncm-footer-hint">Bell shows it in the panel. Email lands in your inbox.</span>
        <button class="ncm-done" @click="emitClose">Done</button>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  CREATOR_NOTIFICATION_GROUPS,
  listNotificationsByGroup,
  type CreatorNotificationEvent,
  type CreatorNotificationPolicy,
} from '@/config/creatorNotificationCatalogue'
import { useNotificationPoliciesStore, type PolicyScope } from '@/store/notificationPolicies.store'
import { campaignsService, type ManagedCampaign } from '@/services/campaigns'

type PolicyChannel = keyof CreatorNotificationPolicy

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const store = useNotificationPoliciesStore()
const { activeScope } = storeToRefs(store)

const campaigns = ref<ManagedCampaign[]>([])
const campaignsLoading = ref(false)
const campaignsError = ref<string | null>(null)

const currentScopeLabel = computed(() => {
  if (activeScope.value === 'default') return 'all campaigns'
  return campaigns.value.find(c => c.id === activeScope.value)?.name ?? 'this campaign'
})

watch(
  () => props.open,
  async open => {
    if (!open) return
    if (campaigns.value.length > 0 || campaignsLoading.value) return
    campaignsLoading.value = true
    campaignsError.value = null
    try {
      campaigns.value = await campaignsService.listCampaigns()
    } catch (err) {
      campaignsError.value = err instanceof Error ? err.message : 'Unknown error'
    } finally {
      campaignsLoading.value = false
    }
  },
  { immediate: true }
)

function policyOf(eventId: CreatorNotificationEvent): CreatorNotificationPolicy {
  return store.getPolicy(activeScope.value, eventId)
}

function setChannel(
  eventId: CreatorNotificationEvent,
  channel: PolicyChannel,
  next: boolean
) {
  const current = policyOf(eventId)
  store.setPolicy(activeScope.value, eventId, { ...current, [channel]: next })
}

function onScopeChange(value: string) {
  store.setScope(value as PolicyScope)
}

function onResetScope() {
  store.resetScope(activeScope.value)
}

function emitClose() {
  emit('close')
}
</script>

<style scoped>
/* IR brand tokens (Inner Reality 2026):
   bg #181b2f, card #2c3e50, pastel green #96ffc9, vivid azure #50e5ff,
   white #ffffff, radius 29px, Montserrat font, ↗ arrow signature mark. */

.ncm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(24, 27, 47, 0.78);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  font-family: 'Montserrat', var(--font-sans, system-ui), sans-serif;
  font-weight: 300;
  padding: 24px;
}

.ncm-container {
  width: 100%;
  max-width: 740px;
  max-height: calc(100vh - 48px);
  background: #2c3e50;
  border-radius: 29px;
  display: flex;
  flex-direction: column;
  color: #ffffff;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  border: 1px solid rgba(150, 255, 201, 0.08);
}

.ncm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 28px 14px;
  gap: 16px;
}
.ncm-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.ncm-arrow {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #96ffc9;
  color: #181b2f;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.ncm-title {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: #ffffff;
}
.ncm-subtitle {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.62);
  font-weight: 300;
  line-height: 1.5;
  max-width: 540px;
}
.ncm-close {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.78);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
}
.ncm-close:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }

.ncm-scope-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 28px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.ncm-scope-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 700;
}
.ncm-select-wrap {
  position: relative;
  flex: 1;
}
.ncm-scope-select {
  width: 100%;
  background: #181b2f;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-family: inherit;
  font-weight: 400;
  font-size: 13px;
  padding: 10px 32px 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}
.ncm-scope-select:focus {
  outline: none;
  border-color: rgba(150, 255, 201, 0.5);
  box-shadow: 0 0 0 3px rgba(150, 255, 201, 0.18);
}
/* Force readable colours on the native option dropdown — UA stylesheets default
   to white-on-white on some dark themes. Most browsers honour this on desktop. */
.ncm-scope-select option,
.ncm-scope-select optgroup {
  background: #181b2f;
  color: #ffffff;
  font-family: inherit;
}
.ncm-scope-select optgroup {
  font-style: normal;
  font-weight: 700;
  color: #96ffc9;
}
.ncm-select-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.ncm-revert-all {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  border-radius: 12px;
  padding: 9px 14px;
  cursor: pointer;
  white-space: nowrap;
}
.ncm-revert-all:hover { background: rgba(255, 255, 255, 0.07); color: #fff; }

.ncm-empty {
  padding: 16px 28px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
}
.ncm-empty--error { color: #fa9696; }

.ncm-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 12px 28px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.ncm-group__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}
.ncm-arrow--mini {
  width: 22px;
  height: 22px;
  border-radius: 7px;
  margin-top: 1px;
}
.ncm-group__header h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
}
.ncm-group__header p {
  margin: 2px 0 0;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 300;
}

.ncm-event-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ncm-event {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 14px;
  background: #181b2f;
  transition: background 0.12s ease;
}
.ncm-event:hover { background: #1f2338; }

.ncm-event__meta { flex: 1; min-width: 0; }
.ncm-event__title-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ncm-event__title { font-size: 13px; font-weight: 700; color: #fff; }
.ncm-event__override {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #181b2f;
  background: #96ffc9;
  padding: 3px 7px;
  border-radius: 7px;
  cursor: pointer;
  border: none;
  font-family: inherit;
}
.ncm-event__override:hover { background: #b3ffd6; }
.ncm-event__desc {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 300;
  line-height: 1.5;
  margin-top: 2px;
}

.ncm-event__toggles { display: flex; gap: 8px; flex-shrink: 0; }

.ncm-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 76px;
  height: 32px;
  padding: 0 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.ncm-toggle:hover { background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.85); }
.ncm-toggle__label { line-height: 1; }
.ncm-toggle--in-app.ncm-toggle--on {
  background: #50e5ff;
  border-color: #50e5ff;
  color: #181b2f;
}
.ncm-toggle--email.ncm-toggle--on {
  background: #96ffc9;
  border-color: #96ffc9;
  color: #181b2f;
}

.ncm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 28px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.ncm-footer-hint {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 300;
}

.ncm-done {
  background: #96ffc9;
  border: none;
  color: #181b2f;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  border-radius: 12px;
  padding: 10px 24px;
  cursor: pointer;
}
.ncm-done:hover { background: #b3ffd6; }
</style>
